// src/components/dashboard/DashboardHome.tsx
// UPDATED — adds real chat-based pending approvals (GET
// /dashboard/chat/chats/pending) as a third data source, alongside the
// existing outcomes and timeline threads fetches. Closes the gap the
// Approvals stress test surfaced: a stuck chat approval (e.g. "install
// this kit" left unresolved) previously had zero presence on Home —
// nothing counted it, nothing called it out, the only way to discover
// it was reopening the exact chat it happened in.
//
// Deliberately kept as a SEPARATE stat and callout from the existing
// Timeline-thread "needs attention" one, not merged into one number.
// Per the same design decision DashboardApprovals.tsx documents:
// MessageThreadSummary (counterparty negotiation state) and chat
// pendingApproval (a paused tool call in the owner's own FDW
// conversation) are structurally different concepts with no natural
// join — collapsing them into one count would overclaim a relationship
// that doesn't exist in the data.
//
// MIGRATED — local FDW_BASE / dashboardToken / getDashboardToken /
// authedFetch block removed in favor of the shared dashboardApi
// client. Three call sites in the Promise.all below:
//   '/dashboard/outcomes'            → '/outcomes'            (data route, now under /api)
//   '/dashboard/timeline/threads'    → '/timeline/threads'    (data route, now under /api)
//   '/dashboard/chat/chats/pending'  → unchanged, full path via authedFetchRaw
//     — chat/* was deliberately NOT moved under /api in the prefix
//     migration (see useDashboardChat.ts), so this one call site uses
//     authedFetchRaw with its full original path rather than authedFetch.
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Plus, ArrowRight, CheckSquare } from 'lucide-react';
import { authedFetch, authedFetchRaw } from '../../services/dashboardApi';

type WorkMode = 'observe' | 'ask_first' | 'complete_within_limits';
type ThreadStatus = 'new' | 'negotiating' | 'committed' | 'settled';

interface Outcome {
  id: string;
  status: 'draft' | 'live' | 'paused';
  defaultWorkMode: WorkMode;
}

interface MessageThreadSummary {
  threadId: string;
  counterpartyId: string;
  status: ThreadStatus;
  unread: boolean;
  lastMessageAt: string;
}

interface PendingChatApproval {
  chatId: string;
  title: string;
  updatedAt: string | null;
  pendingApproval: {
    tool_use_id: string;
    name: string;
    input: any;
  };
}

const WORK_MODE_LABELS: Record<WorkMode, string> = {
  observe: 'Observe',
  ask_first: 'Ask First',
  complete_within_limits: 'Complete Within Limits',
};

const WORK_MODE_DESCRIPTIONS: Record<WorkMode, string> = {
  observe: "Agents can learn about your business but can't act.",
  ask_first: 'Agents prepare work and wait for your approval before proceeding.',
  complete_within_limits: 'Agents complete scoped work automatically, inside the boundaries you\'ve set.',
};

function mostCommonWorkMode(outcomes: Outcome[]): WorkMode | null {
  const liveOutcomes = outcomes.filter((o) => o.status === 'live');
  if (liveOutcomes.length === 0) return null;

  const counts: Record<WorkMode, number> = { observe: 0, ask_first: 0, complete_within_limits: 0 };
  liveOutcomes.forEach((o) => { counts[o.defaultWorkMode]++; });

  return (Object.keys(counts) as WorkMode[]).reduce((a, b) => (counts[a] >= counts[b] ? a : b));
}

export const DashboardHome: React.FC = () => {
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [threads, setThreads] = useState<MessageThreadSummary[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<PendingChatApproval[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [outcomesRes, threadsRes, approvalsRes] = await Promise.all([
          authedFetch('/outcomes', { method: 'POST' }),
          authedFetch('/timeline/threads', { method: 'POST' }),
          authedFetchRaw('/dashboard/chat/chats/pending'),
        ]);

        if (!outcomesRes.ok) throw new Error('Failed to load outcomes.');
        if (!threadsRes.ok) throw new Error('Failed to load timeline.');
        // Approvals fetch is treated as non-fatal — a hiccup fetching
        // it shouldn't block the rest of Home from rendering, same
        // posture as the chat history list's refreshChats().
        const approvalsOk = approvalsRes.ok;

        const outcomesData = await outcomesRes.json();
        const threadsData = await threadsRes.json();
        const approvalsData = approvalsOk ? await approvalsRes.json() : { pending: [] };

        if (!cancelled) {
          setOutcomes(outcomesData.outcomes ?? []);
          setThreads(threadsData.threads ?? []);
          setPendingApprovals(approvalsData.pending ?? []);
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Could not load your dashboard.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (isLoading) return <p className="text-sm text-brand-gray font-mono">Loading your dashboard…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;

  const liveOutcomeCount = outcomes.filter((o) => o.status === 'live').length;
  const hasNoOutcomes = outcomes.length === 0;

  // Approximation, not an exact "needs approval" count — see file header.
  const needsAttentionThreads = threads.filter(
    (t) => t.unread && (t.status === 'new' || t.status === 'negotiating')
  );

  const autonomyPosture = mostCommonWorkMode(outcomes);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-brand-black">Home</h1>
          <p className="text-sm text-brand-charcoal font-light mt-1">
            A snapshot of what's active and what needs you.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            size="sm"
            render={<Link to="/dashboard/outcomes/new" className="flex items-center gap-1.5" />}
          >
            <Plus className="w-4 h-4" />
            Create new package
          </Button>
        </div>
      </div>

      {hasNoOutcomes && (
        <div className="rounded-xl border border-dashed border-brand-border bg-brand-surface p-10 text-center">
          <p className="text-sm text-brand-charcoal font-light mb-4">
            You haven't published an outcome yet. Once you do, this page becomes your control center — active work and unread threads that need you, all in one place.
          </p>
          <Button
            variant="primary"
            size="sm"
            render={<Link to="/dashboard/outcomes/new" />}
          >
            Define your first outcome
          </Button>
        </div>
      )}

      {/* ── QUICK STATS ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl border border-brand-border bg-brand-white p-5">
          <p className="text-2xl font-semibold text-brand-black">{liveOutcomeCount}</p>
          <p className="text-xs text-brand-gray font-mono mt-1">Live packages</p>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-white p-5">
          <p className={`text-2xl font-semibold ${pendingApprovals.length > 0 ? 'text-amber-700' : 'text-brand-black'}`}>
            {pendingApprovals.length}
          </p>
          <p className="text-xs text-brand-gray font-mono mt-1">Waiting on your approval</p>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-white p-5">
          <p className={`text-2xl font-semibold ${needsAttentionThreads.length > 0 ? 'text-amber-700' : 'text-brand-black'}`}>
            {needsAttentionThreads.length}
          </p>
          <p className="text-xs text-brand-gray font-mono mt-1">Unread, may need you</p>
        </div>
        <div className="rounded-xl border border-brand-border bg-brand-white p-5">
          <p className="text-2xl font-semibold text-brand-black">{threads.length}</p>
          <p className="text-xs text-brand-gray font-mono mt-1">Total conversations</p>
        </div>
      </div>

      {/* ── PENDING APPROVALS CALLOUT ─────────────────────────────── */}
      {pendingApprovals.length > 0 && (
        <Link
          to="/dashboard/approvals"
          className="flex items-center justify-between rounded-xl border-2 border-brand-accent bg-white p-6 shadow-glow hover:bg-brand-surface transition-colors"
        >
          <div className="flex items-start gap-3">
            <CheckSquare className="w-5 h-5 text-brand-accent mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-brand-black">
                {pendingApprovals.length} action{pendingApprovals.length === 1 ? '' : 's'} waiting on your approval
              </p>
              <p className="text-sm text-brand-charcoal font-light mt-1">
                From conversations with FDW — nothing happens until you decide.
              </p>
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-brand-black shrink-0" />
        </Link>
      )}

      {/* ── TIMELINE CALLOUT ──────────────────────────────────────── */}
      {needsAttentionThreads.length > 0 && (
        <Link
          to="/dashboard/timeline"
          className="flex items-center justify-between rounded-xl border-2 border-brand-accent bg-white p-6 shadow-glow hover:bg-brand-surface transition-colors"
        >
          <div>
            <p className="text-sm font-medium text-brand-black">
              {needsAttentionThreads.length} unread conversation{needsAttentionThreads.length === 1 ? '' : 's'} may need a decision
            </p>
            <p className="text-sm text-brand-charcoal font-light mt-1">
              Open the Timeline to see what's waiting.
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-brand-black shrink-0" />
        </Link>
      )}

      {/* ── AUTONOMY POSTURE ──────────────────────────────────────── */}
      {autonomyPosture && (
        <div className="rounded-xl border border-brand-border bg-brand-white p-6">
          <p className="text-xs font-mono uppercase tracking-widest text-brand-gray mb-3">
            Most common work mode across your live packages
          </p>
          <p className="text-base font-medium text-brand-black mb-1">
            {WORK_MODE_LABELS[autonomyPosture]}
          </p>
          <p className="text-sm text-brand-charcoal font-light leading-relaxed">
            {WORK_MODE_DESCRIPTIONS[autonomyPosture]}
          </p>
        </div>
      )}
    </div>
  );
};